import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { EmailVerificationService } from '../../auth/services/email-verification.service';
import { AuthStore } from '../../auth/services/auth-store.service';
import { LanguageService } from '../../../transloco/language.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-email-verification-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    TranslocoModule
  ],
  template: `
    <div class="modal-content" [class.rtl]="isRtl()" [attr.dir]="direction()">
      <!-- Header -->
      <div class="modal-header">
        <div class="icon-wrapper">
          <mat-icon class="mail-icon">mark_email_unread</mat-icon>
        </div>
        <h2 class="modal-title">{{ 'EMAIL_VERIFICATION_MODAL.TITLE' | transloco }}</h2>
        <p class="modal-subtitle">{{ 'EMAIL_VERIFICATION_MODAL.SUBTITLE' | transloco }}</p>
      </div>

      <!-- Email display -->
      <div class="email-display">
        <span class="email-text">{{ userEmail() }}</span>
      </div>

      <!-- Hint text -->
      <p class="hint-text">{{ 'EMAIL_VERIFICATION_MODAL.HINT_TEXT' | transloco }}</p>

      <!-- Actions -->
      <div class="modal-actions">
        <button
          class="action-btn primary"
          (click)="checkVerification()"
          [disabled]="isChecking || !canCheck"
          [class.loading]="isChecking">
          <mat-icon *ngIf="isChecking" class="spinning">sync</mat-icon>
          <span>{{ isChecking ? ('EMAIL_VERIFICATION_MODAL.BUTTONS.CHECKING' | transloco) : ('EMAIL_VERIFICATION_MODAL.BUTTONS.VERIFIED' | transloco) }}</span>
        </button>

        <!-- Not verified hint -->
        <p class="not-verified-hint" *ngIf="showNotVerifiedHint">
          {{ 'EMAIL_VERIFICATION_MODAL.NOT_VERIFIED_YET' | transloco }}
        </p>

        <button
          class="action-btn secondary"
          (click)="resendEmail()"
          [disabled]="!canResend || isSending">
          <mat-icon *ngIf="isSending" class="spinning">sync</mat-icon>
          <span>{{ getResendButtonText() }}</span>
        </button>
      </div>
    </div>
  `,
  styleUrls: ['./email-verification-modal.component.scss']
})
export class EmailVerificationModalComponent implements OnInit, OnDestroy {
  private languageService = inject(LanguageService);
  private translocoService = inject(TranslocoService);
  private router = inject(Router);

  isRtl = this.languageService.isRtlSignal;
  direction = this.languageService.directionSignal;

  userEmail = computed(() => {
    const user = this.authStore.getCurrentUser();
    return user?.email || '';
  });

  isChecking = false;
  isSending = false;
  canCheck = true; // For 3 second cooldown on check button
  canResend = false; // Start with false, enable after 5 minutes
  cooldownSeconds = 300; // 5 minutes = 300 seconds
  showNotVerifiedHint = false;

  private subscriptions = new Subscription();
  private countdownInterval?: any;

  constructor(
    private emailVerificationService: EmailVerificationService,
    private authStore: AuthStore,
    private dialogRef?: MatDialogRef<EmailVerificationModalComponent>
  ) {}

  ngOnInit(): void {
    // Start the 5-minute countdown
    this.startCountdown();
  }

  private startCountdown(): void {
    this.countdownInterval = setInterval(() => {
      if (this.cooldownSeconds > 0) {
        this.cooldownSeconds--;
        if (this.cooldownSeconds === 0) {
          this.canResend = true;
        }
      }
    }, 1000);
  }

  checkVerification(): void {
    if (!this.canCheck) return;

    this.isChecking = true;
    this.canCheck = false;

    this.emailVerificationService.checkVerification().subscribe({
      next: (isVerified) => {
        this.isChecking = false;

        if (isVerified) {
          this.closeModal();
          this.router.navigate(['/wizard/step1']);
        } else {
          this.showNotVerifiedHint = true;
          setTimeout(() => {
            this.canCheck = true;
          }, 3000);
        }
      },
      error: () => {
        this.isChecking = false;
        console.error('Error checking verification status');
        setTimeout(() => {
          this.canCheck = true;
        }, 3000);
      }
    });
  }

  resendEmail(): void {
    if (!this.canResend) return;

    this.isSending = true;

    this.emailVerificationService.resendVerificationEmail().subscribe({
      next: (response) => {
        this.isSending = false;
        // Reset countdown after successful resend
        this.cooldownSeconds = 300;
        this.canResend = false;
        console.log('Verification email sent!', response.message);
      },
      error: (error) => {
        this.isSending = false;
        console.error('Failed to send verification email', error);
      }
    });
  }

  getResendButtonText(): string {
    if (this.isSending) {
      return this.translocoService.translate('EMAIL_VERIFICATION_MODAL.BUTTONS.SENDING');
    }
    if (this.cooldownSeconds > 0) {
      const minutes = Math.floor(this.cooldownSeconds / 60);
      const seconds = this.cooldownSeconds % 60;
      const resendText = this.translocoService.translate('EMAIL_VERIFICATION_MODAL.BUTTONS.RESEND');
      return `${resendText} (${minutes}:${seconds.toString().padStart(2, '0')})`;
    }
    return this.translocoService.translate('EMAIL_VERIFICATION_MODAL.BUTTONS.RESEND');
  }

  getTimerOffset(): string {
    const circumference = 2 * Math.PI * 18; // radius = 18
    const offset = circumference - (this.cooldownSeconds / 300) * circumference; // 300 seconds = 5 minutes
    return offset.toString();
  }

  closeModal(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }
}
